import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import palette from '../../../theme/palette'
import { count, shortTime } from './alertUtils'

/**
 * What a Proship run produced — Preview (rendered, nothing sent) or Send now (queued). One block per recipient ×
 * message, exactly as it went (or would go) out, so the parallel run is a side-by-side read against Message history.
 */
export default function RunResultDialog({ open, result, title, onClose }) {
  const r = result || {}
  const msgs = r.messages || []
  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pb: 1.25, borderBottom: '3px solid #2065D1' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ width: 4, height: 28, borderRadius: 2, background: '#2065D1', flexShrink: 0 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1a1a2e' }}>{title || (r.dryRun ? 'Preview' : 'Sent')}</Typography>
          {r.day && <Chip size='small' variant='outlined' label={`for ${r.day}`} sx={{ height: 20, fontSize: 11 }} />}
          {r.dryRun
            ? <Chip size='small' color='info' label='Preview — nothing sent' sx={{ height: 20, fontSize: 11 }} />
            : r.messagesFailed
              ? <Chip size='small' color='error' label={`${count(r.messagesQueued)} queued · ${count(r.messagesFailed)} failed`} sx={{ height: 20, fontSize: 11 }} />
              : <Chip size='small' color='success' label={`${count(r.messagesQueued)} queued to WhatsApp`} sx={{ height: 20, fontSize: 11 }} />}
        </Box>
        <IconButton size='small' onClick={onClose} aria-label='Close'><CloseIcon fontSize='small' /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {r.note && <Alert severity={r.messagesFailed ? 'error' : 'info'} sx={{ mb: 2, py: 0.5 }}>{r.note}</Alert>}
        <Typography variant='caption' sx={{ color: 'text.disabled', display: 'block', mb: 1.5 }}>
          Computed {r.startedAt ? `${shortTime(r.startedAt)} IST` : ''} in {r.ms || 0} ms · {count(r.bodies)} message body{r.bodies === 1 ? '' : 'ies'} × {count(r.recipients)} active recipient{r.recipients === 1 ? '' : 's'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {msgs.map((m, i) => (
            <Box key={m.uuid || i} sx={{ border: `1px solid ${palette.grey[300]}`, borderRadius: 1, p: 1.5, bgcolor: palette.grey[100] }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {m.displayName || m.mobile}{m.displayName && <Box component='span' sx={{ color: 'text.disabled', fontFamily: 'monospace', ml: 1 }}>{m.mobile}</Box>}
                </Typography>
                {m.uuid && <Typography variant='caption' sx={{ color: 'text.disabled', fontFamily: 'monospace' }} title='interaction_audit.identifier'>{m.uuid}</Typography>}
                {m.error && <Chip size='small' color='error' variant='outlined' label='Not queued' sx={{ height: 20, fontSize: 11 }} />}
              </Box>
              {m.error && <Typography variant='caption' sx={{ color: palette.error.main, display: 'block', mb: 0.5 }}>{m.error}</Typography>}
              <Typography variant='body2' component='pre' sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', m: 0 }}>{m.renderedText}</Typography>
            </Box>
          ))}
          {!msgs.length && !r.note && <Typography variant='body2' sx={{ color: 'text.disabled' }}>No messages.</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button size='small' variant='outlined' onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
